import { cn } from "@istari-frontend/shared";
import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@istari-frontend/ui";
import { ArrowLeft, Maximize2, Minimize2, SquareArrowOutUpRight, XIcon } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router";

import type { ConversationArtifact } from "~/ai-chat/model/conversation-artifacts";
import type { FilePreviewRequest } from "~/ai-chat/model/file-preview-types";
import { extensionOf } from "~/ai-chat/model/save-resource-action";
import { FileViewer } from "~/components/file-viewer";
import { useResource } from "~/resource/data-source/use-resource";
import { useResourceRevisionContent } from "~/resource/data-source/use-resource-revision-content";
import { routes } from "~/routes";

// What the canvas shows: a library resource fetched by id, or an unsaved draft the
// assistant proposed, rendered straight from its inline content.
export type ArtifactPreviewTarget =
  ({ kind: "resource" } & FilePreviewRequest) | { kind: "draft"; name: string; content: string };

// Every artifact card previews here, saved ones by fetching the resource and drafts from
// their inline content; the library is reachable from the preview header, not the card.
export function artifactPreviewTarget(artifact: ConversationArtifact): ArtifactPreviewTarget {
  return artifact.kind === "saved"
    ? { kind: "resource", resourceId: artifact.resourceId, name: artifact.name }
    : { kind: "draft", name: artifact.name, content: artifact.content };
}

interface AiChatCanvasProps {
  file: ArtifactPreviewTarget;
  onClose: () => void;
  // Returns to the artifact list the file was opened from; absent when there is no list.
  onBack?: () => void;
  // Maximized = the canvas takes the whole workspace (the page hides the conversation and
  // the list behind it) instead of a fixed 480px column.
  wide?: boolean;
  onToggleMaximize?: () => void;
  className?: string;
}

// The artifact canvas: a library resource referenced in the chat, fetched and rendered
// full-height with the resource details page's FileViewer (same per-type renderers +
// unsupported/too-large fallbacks), or an unsaved draft rendered from its inline content.
export function AiChatCanvas({ file, onClose, onBack, wide, onToggleMaximize, className }: AiChatCanvasProps) {
  const resourceId = file.kind === "resource" ? file.resourceId : undefined;
  const resourceQuery = useResource(resourceId, file.kind === "resource");
  const revisionId = resourceQuery.data?.revisionId;
  const contentQuery = useResourceRevisionContent(resourceId, revisionId, !!revisionId);

  // FileViewer treats a string as a URL to fetch, so a draft's text goes in as bytes.
  const draftText = file.kind === "draft" ? file.content : undefined;
  const draftBytes = useMemo(() => (draftText === undefined ? null : new TextEncoder().encode(draftText)), [draftText]);
  const content = file.kind === "draft" ? draftBytes : (contentQuery.data?.content ?? null);
  const extension = file.kind === "draft" ? extensionOf(file.name) : contentQuery.data?.extension;
  const failed = file.kind === "resource" && (resourceQuery.isError || contentQuery.isError);
  const loading = file.kind === "resource" && (resourceQuery.isLoading || contentQuery.isLoading);

  return (
    <section
      aria-label={`Artifact preview: ${file.name}`}
      className={cn(
        "border-border bg-sidebar flex h-full flex-col border-l",
        wide ? "min-w-0 flex-1" : "w-[480px] shrink-0",
        className,
      )}
    >
      <header className="border-border flex h-11 shrink-0 items-center justify-between gap-2 border-b pr-3.5 pl-3">
        <div className="flex min-w-0 items-center gap-1">
          {onBack ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-sm" onClick={onBack} aria-label="Back to artifacts">
                  <ArrowLeft className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Back to artifacts</TooltipContent>
            </Tooltip>
          ) : null}
          <span className="text-muted-foreground truncate text-sm font-medium">{file.name}</span>
        </div>
        <div className="flex items-center gap-2">
          {file.kind === "resource" ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-sm" asChild aria-label={`Open ${file.name} in your library`}>
                  <Link to={`${routes.Resources}/${file.resourceId}`}>
                    <SquareArrowOutUpRight className="size-4" />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Open in library</TooltipContent>
            </Tooltip>
          ) : null}
          {onToggleMaximize ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={onToggleMaximize}
                  aria-label={wide ? "Collapse artifact" : "Maximize artifact"}
                  aria-pressed={wide}
                >
                  {wide ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{wide ? "Collapse" : "Maximize"}</TooltipContent>
            </Tooltip>
          ) : null}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close artifact preview">
                <XIcon className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Close</TooltipContent>
          </Tooltip>
        </div>
      </header>
      {/* FileViewer fills its parent (h-full min-h-0), so the body needs a bounded, scrollable box. */}
      <div className="bg-muted/20 min-h-0 flex-1 overflow-y-auto">
        {failed ? (
          <div className="text-muted-foreground flex size-full items-center justify-center text-sm">
            Couldn&apos;t load this artifact.
          </div>
        ) : (
          <FileViewer
            content={content}
            loading={loading}
            versionId={revisionId}
            properties={{
              displayFileName: file.name,
              extension,
              size: content?.length,
            }}
          />
        )}
      </div>
    </section>
  );
}
